import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Copy, Trash2, Check, Link2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareDialog({ task, open, onClose, onChange }) {
  const [token, setToken] = useState(task?.share_token || null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setToken(task?.share_token || null);
    setCopied(false);
  }, [task, open]);

  const shareUrl = token ? `${window.location.origin}/share/${token}` : "";

  const setEnabled = async (enabled) => {
    if (!task) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/tasks/${task.id}/share`, { enabled });
      const newToken = res.data?.share_token || null;
      setToken(newToken);
      onChange?.(newToken);
      toast.success(enabled ? "Sharing enabled" : "Sharing disabled");
    } catch {
      toast.error("Couldn't update sharing");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — copy it manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none max-w-md p-0 gap-0 border border-zinc-300"
        data-testid="share-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
          <DialogTitle className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            Share task
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          <div>
            <p className="text-sm text-zinc-900 font-medium leading-snug mb-1">
              {task?.title}
            </p>
            <p className="text-xs text-zinc-500">
              Generate a read-only public link. Anyone with the link can see the task and its subtasks — no sign-in required. Revoke any time.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-zinc-200">
            <div>
              <label className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                Public link
              </label>
              <p className="text-xs text-zinc-500 mt-0.5">
                {token ? "Live — anyone with this URL can view" : "Off — task is private"}
              </p>
            </div>
            <Switch
              checked={!!token}
              disabled={busy}
              onCheckedChange={setEnabled}
              data-testid="share-toggle"
            />
          </div>

          {token && (
            <div className="space-y-2" data-testid="share-link-block">
              <div className="flex items-stretch border border-zinc-300">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 px-3 py-2.5 text-xs font-mono text-zinc-700 outline-none bg-zinc-50"
                  data-testid="share-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={copy}
                  className="px-3 border-l border-zinc-300 text-zinc-700 hover:text-[#002FA7] hover:bg-blue-50 transition-colors flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] font-medium"
                  data-testid="share-copy-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" strokeWidth={2} /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Copy
                    </>
                  )}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(false)}
                disabled={busy}
                className="flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] text-red-700 hover:text-red-900 disabled:opacity-50"
                data-testid="share-revoke-btn"
              >
                <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                Revoke link
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-200">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.12em] px-4 py-2.5 text-zinc-700 hover:bg-zinc-100"
            data-testid="share-done-btn"
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
